import React, { useRef, useState, useCallback, useEffect } from 'react'
import {
  AlphaCollection,
  NumericCollection,
  KeyBoardDistributionEnum,
} from '../../components/Keyboard/types'
import {
  FocusContext,
  getCurrentFocusKey,
  setFocus,
  useFocusable,
} from '@noriginmedia/norigin-spatial-navigation'
import type { SearchScreenHeaderProps } from './types'
import { useAppDispatch, useAppSelector } from '../../hooks/useStore'
import {
  KeyTypeEnum,
  useKeyDown,
} from '../../../../shared/providers/KeyDownProvider'
import { useSidePanel } from '../../components/SidePanel/SidePanelProvider'

const FIRST_ALPHA_KEY = 'ALPHA_KEY_1'
const FIRST_NUMERIC_KEY = 'NUMERIC_KEY_1'
const ALPHA = 'ALPHA'
const NUMERIC = 'NUMERIC'
const SAVE_SEARCH_QUERY_ACTION_NAME = 'SAVE_SEARCH_QUERY'
const SEARCH_SCREEN_HEADER = 'SEARCH_SCREEN_HEADER'

function SearchScreenHeader({
  children,
  getSearchResults,
  handleSearchCleared,
  emptyCollectionResponse,
  handleLeftArrowExit,
  onFocus,
}: SearchScreenHeaderProps) {
  const { isSidePanelOpened, focusSidePanel } = useSidePanel()
  const [searchQuery, setSearchQuery] = useState('')
  const [showCursor, setShowCursor] = useState(false)
  const searchQueryRedux = useAppSelector((state) => state.search.searchQuery)
  const dispatch = useAppDispatch()
  const oldSearchQuery = useRef('')

  const {
    ref: header,
    focusKey,
    hasFocusedChild,
  } = useFocusable({
    focusKey: SEARCH_SCREEN_HEADER,
    trackChildren: true,
    onBlur: (_arg1, _arg2, { event }) => {
      if (event && (event as KeyboardEvent).key === 'ArrowDown') {
        dispatch({
          type: SAVE_SEARCH_QUERY_ACTION_NAME,
          payload: { searchQuery },
        })
      }
    },
    onFocus,
  })

  useEffect(() => {
    if (hasFocusedChild) {
      setShowCursor(true)
    } else {
      setShowCursor(false)
    }
  }, [hasFocusedChild])

  useEffect(() => {
    handleSearchQueryChange()
  }, [searchQuery])

  useEffect(() => {
    if (searchQueryRedux) {
      setSearchQuery(searchQueryRedux)
      handleSearchQueryChange()
    }
  }, [])

  const handleSearchQueryChange = () => {
    const searchQueryChanged = searchQuery !== oldSearchQuery.current
    const searchQueryCleared =
      searchQuery.length === 0 && oldSearchQuery.current.length > 0

    if (searchQueryChanged && searchQuery.length >= 2) {
      getSearchResults(searchQuery)
    }

    if (searchQueryCleared) {
      handleSearchCleared()
    }

    if (searchQueryChanged) {
      oldSearchQuery.current = searchQuery
    }
  }

  const onEnterPress = useCallback(
    (keyString: AlphaCollection | NumericCollection) => {
      if (
        [AlphaCollection.spaceAlpha, NumericCollection.spaceNumeric].includes(
          keyString
        )
      ) {
        setSearchQuery((prev) => prev + ' ')
      } else if (
        [AlphaCollection.deleteAlpha, NumericCollection.deleteNumeric].includes(
          keyString
        )
      ) {
        setSearchQuery((prev) => prev.slice(0, -1))
      } else if (
        Object.values(AlphaCollection).includes(keyString as AlphaCollection)
      ) {
        setSearchQuery((prev) => (prev.length < 50 ? prev + keyString : prev))
      } else if (
        Object.values(NumericCollection).includes(
          keyString as NumericCollection
        )
      ) {
        setSearchQuery((prev) => (prev.length < 50 ? prev + keyString : prev))
      }
    },
    [setSearchQuery]
  )

  const onArrowPress = useCallback(
    (direction: string, props: { index: number }) => {
      if (props.index === 0 && direction === 'left') {
        handleLeftArrowExit()
      }
      return true
    },
    [header, emptyCollectionResponse]
  )

  useKeyDown(
    KeyTypeEnum.Back,
    () => {
      const currentFocusKey = getCurrentFocusKey()
      if (!isSidePanelOpened) {
        if (
          currentFocusKey &&
          currentFocusKey.includes(ALPHA) &&
          currentFocusKey !== FIRST_ALPHA_KEY
        ) {
          setFocus(FIRST_ALPHA_KEY)
        } else if (
          currentFocusKey &&
          currentFocusKey.includes(NUMERIC) &&
          currentFocusKey !== FIRST_NUMERIC_KEY
        ) {
          setFocus(FIRST_NUMERIC_KEY)
        } else if (
          currentFocusKey &&
          [FIRST_ALPHA_KEY, FIRST_NUMERIC_KEY].includes(currentFocusKey)
        ) {
          if (searchQuery.length > 0) {
            setSearchQuery('')
          } else {
            handleLeftArrowExit()
            focusSidePanel()
          }
        } else if (
          [
            KeyBoardDistributionEnum.numeric,
            KeyBoardDistributionEnum.alpha,
          ].includes(currentFocusKey as KeyBoardDistributionEnum)
        ) {
          handleLeftArrowExit()
          focusSidePanel()
        } else {
          setFocus(SEARCH_SCREEN_HEADER)
        }
      }
    },
    { stopPropagation: !isSidePanelOpened }
  )

  return (
    <FocusContext.Provider value={focusKey}>
      <div className="search-screen__header" ref={header}>
        <div className="search-screen__search-controls">
          {children(searchQuery, showCursor, onEnterPress, onArrowPress)}
        </div>
      </div>
    </FocusContext.Provider>
  )
}

export default SearchScreenHeader
